import {
    InfoWindow,
    useMap,
} from '@vis.gl/react-google-maps';
import DmsCoordinates from "dms-conversion";
import parse from 'html-react-parser';
import { isMobile } from 'react-device-detect';
import { isNumeric } from '../../../helpers/ObjectHelpers';
import ShipImage from '../../ShipImage';
import MapPropertiesService from '../services/MapPropertiesService';
import { useEffect, useState } from 'react';
import MapApiRepository from '../repositories/MapApiRepository';
import moment from "moment";
import { getElementById } from '../../../helpers/ElementHelpers';
import MapElementService, { IMapElementService } from '../services/MapElementService';
import Spacer from '../../Spacer';
import { useMapId, useShowInfo, useHasQuayManualAccess, useShowLocationQuayManual } from '../hooks/useMapStore';

export const MapMarkerInfoWindow = () => {

    const mapId = useMapId();
    const stateShowInfo = useShowInfo();

    const map = useMap();

    const mapMarkerTypes = MapPropertiesService.markerTypes;
    const mapProperties = MapPropertiesService.properties;

    const [position, setPosition] = useState<any | null>(null);
    const [marker, setMarker] = useState<any | null>(null);

    const markerOffset = (marker:any) => {
        switch (marker.type) {
            case mapMarkerTypes.Ship:
                return marker.speedOverGround <= mapProperties.MinimumSpeed ? 8 : 12;
            case mapMarkerTypes.Location:
                return 16;
            case mapMarkerTypes.CertificateLocation:
                return 25;
            default:
                return 20;
        }
    }

    const calculateLatitudeOffset = (marker: any): number => {
        if (!map) return 0;

        const elem: any = getElementById(mapId);
        if (!elem) return 0;

        const mapHeight = elem.offsetHeight;
        const bounds = map.getBounds();

        if (!bounds) return 0;

        const north = bounds.getNorthEast().lat();
        const south = bounds.getSouthWest().lat();

        const mapDegrees = north - south; // Height in degrees

        const offset = markerOffset(marker);

        return (offset / mapHeight) * mapDegrees;
    }

    const handleShowInfoMessage = (data: any) => {
        if (!map) return;

        if (!data) {
            setPosition(null);
        } else {
            const latOffset = calculateLatitudeOffset(data);
            setPosition({ lat: data.position.lat() + latOffset, lng: data.position.lng() });
        }
        setMarker(data);
    }

    useEffect(() => {
        handleShowInfoMessage(stateShowInfo)
    }, [stateShowInfo]);

    return (
        (!isMobile && marker && position) &&
            <InfoWindow
                position={position}
                maxWidth={200}
                disableAutoPan={true}
                headerDisabled={true}
                headerContent={`${getInfoWindowHeaderContent()}`}
                onCloseClick={() => setMarker(null)}>
                <div className="font-size-md text-truncate">
                    <strong>{getInfoWindowHeaderContent()}</strong>
                </div>
                <Spacer height={5} />
                <MapMarkerInfoWindowContent
                    marker={marker}
                />
            </InfoWindow>
    )

    function getInfoWindowHeaderContent() {
        switch (marker.type) {
            case mapMarkerTypes.Ship:
                return `${marker.description} (${marker.callSign})`;
            default:
                return marker.description;
        }
    }
}

interface MapMarkerInfoWindowContentProps {
    marker: any,
}
const MapMarkerInfoWindowContent = ({
    marker,
}: MapMarkerInfoWindowContentProps) => {

    const { mapId } = useMapId();

    const mapMarkerTypes = MapPropertiesService.markerTypes;
    const id = `googleMapInfoWindow-${mapId}`;

    return (
        (marker) &&
        <div id={id} className={`google-map-info-window google-map-info-window-desktop`}>
                 <div className="google-map-info-window-body">
                    {(() => {
                        switch (marker.type) {
                            case mapMarkerTypes.Ship:
                                return (<MapMarkerInfoWindowShipContent marker={marker}/>)
                            case mapMarkerTypes.Location:
                                return <MapMarkerInfoWindowLocationContent marker={marker} />
                            case mapMarkerTypes.CertificateLocation:
                                return <MapMarkerInfoWindowCertificateLocationContent marker={marker} />
                            default:
                                return <MapMarkerInfoWindowLatLngContent marker={marker} />
                        }
                        })()}
                </div>
            </div>
    )
}

interface MapMarkerInfoWindowShipContentProps {
    marker: any,
}
const MapMarkerInfoWindowShipContent = ({
    marker,
}: MapMarkerInfoWindowShipContentProps) => {

    const speedOverGround = isNumeric(marker.speedOverGround) ? marker.speedOverGround.toFixed(1) : 0;
    const courseOverGround = isNumeric(marker.courseOverGround) ? marker.courseOverGround.toFixed(1) : 0;
    const shipId = isNumeric(marker.shipId) ? marker.shipId : 0;

    const mapElementService: IMapElementService = new MapElementService();

    return (
        <>
            <ShipImage imoNo={marker.imoNo} description={marker.description} canShowDummyImage={false} />
            <Spacer height={5} />
            <div className="k-d-grid k-gap-1">
                <div className="font-size-sm">{mapElementService.getShipTypeDescription(marker)}</div>
                <Spacer height={5} />
                <div className="font-size-sm">
                    COG: {courseOverGround}&#176;
                </div>
                <div className="font-size-sm">
                    SOG: {speedOverGround}kn
                </div>
                <div>
                    <MapMarkerInfoWindowLatLngContent marker={marker} />
                </div>
                <Spacer />
                <div className="font-size-sm text-align-center">
                    {
                        (shipId === 0) &&
                        <strong>{parse('Fart&oslash;yinformasjon er ikke registrert i grunnlagsdata')}</strong>
                    }

                </div>
                <div className="font-size-sm">
                    {
                        (shipId > 0) &&
                        parse("Klikk for &aring; se detaljer")
                    }
                </div>
            </div>
        </>
    )
}

interface MapMarkerInfoWindowCertificateLocationContentProps {
    marker: {
        description: string,
        certificateStatus: string,
        lastPilotageDate: string
    }
}

const MapMarkerInfoWindowCertificateLocationContent = ({
    marker
}: MapMarkerInfoWindowCertificateLocationContentProps) => {

    let lastPilotageDate = " ";
    const obj = moment(marker.lastPilotageDate);
    if (obj.isValid() && obj.year() > 1970)
    {
        lastPilotageDate = obj.format("DD.MM.YYYY");
    }

    return (
        <>
            <div className="google-map-info-window-content">
                <div className="k-d-grid k-gap-4 grid-cols-auto-fr">
                    <MapMarkerInfoWindowLatLngContent marker={marker} />
                </div>
            </div>
            <div className="google-map-info-window-toolbar" style={{ paddingTop: 20 }}>
                <div className="font-size-sm">
                    Sist seilt: {lastPilotageDate}
                </div>
                <div className="font-size-sm">
                    Sertifikatstatus: {marker.certificateStatus}
                </div>
            </div>

        </>
    )
}

interface MapMarkerInfoWindowLocationContentProps {
    marker: any
}
const MapMarkerInfoWindowLocationContent = ({
    marker
}: MapMarkerInfoWindowLocationContentProps) => {

    const [hasQuayManuals, setHasQuayManuals] = useState(false);
    const { hasQuayManualAccess } = useHasQuayManualAccess();

    const showLocationQuayManual = useShowLocationQuayManual();

    useEffect(() => {
        if (!hasQuayManualAccess || !showLocationQuayManual) return;
        const initialize = async () => {
            const response = await MapApiRepository.getQuayManualByLocationAndProximity(marker.id);
            if (response.ok) {
                const result = await response.json();
                setHasQuayManuals(result !== null && result.length > 0);
                marker.hasQuayManuals = result !== null && result.length > 0;
            }
        }
        initialize();
    }, [hasQuayManualAccess, showLocationQuayManual]);

    return (
        <>
            <div className="google-map-info-window-content">
                <div className="k-d-grid k-gap-4 grid-cols-auto-fr">
                    <MapMarkerInfoWindowLatLngContent marker={marker} />
                </div>
            </div>
            {
                (hasQuayManuals) &&
                    <div className="google-map-info-window-toolbar">
                        <div className="font-size-sm" style={{ paddingTop: 20 }}>
                            Klikk for &aring; se kaibok
                        </div>
                    </div>
            }
        </>
    )
}

interface MapMarkerInfoWindowLatLngContentProps {
    marker: any
}
const MapMarkerInfoWindowLatLngContent = ({
    marker
}: MapMarkerInfoWindowLatLngContentProps) => {

    const dmsCoords = new DmsCoordinates(marker.position.lat(), marker.position.lng());
    const { longitude, latitude } = dmsCoords.dmsArrays;

    return (
        <div className="k-d-grid font-size-sm">
            <div>
                {parseCoordinates(latitude)}, {parseCoordinates(longitude)}
            </div>
        </div>
    )

    function parseCoordinates(coordinate: any) {
        const [d, m, s, nsew] = coordinate;
        return (
            parse(`${d}&#176;${m}'${s.toFixed(0)}" ${getCoordinateDirection(nsew)}`)
        )
    }

    function getCoordinateDirection(nsew: string) {
        switch (nsew) {
            case "E":
                return "&Oslash;";
            case "W":
                return "V";
            default:
                return nsew;
        }
    }
}
